import { useCallback, useLayoutEffect, useRef } from 'react'

import { useI18n } from '../../../shared/i18n/useI18n.js'
import { useProductStrings } from '../../../shared/i18n/useProductStrings.js'

import { MAX_PINNED_FAVORITES_DESKTOP, MAX_PINNED_FAVORITES_MOBILE } from '../constants/favoritesConfig.js'
import { CompareColumn, PlaceholderCard } from './CompareColumn.jsx'

import styles from './FavoritesCompareBlock.module.scss'

/**
 * Compare section — grid of pinned products with compare items.
 *
 * @param {object} props
 * @param {array} props.pinnedProducts - array of pinned product objects
 * @param {array} props.compareRows - favorites_comparison.rows from panel settings
 * @param {string} props.projectDataUrl
 * @param {string} props.logoSrc
 * @param {boolean} props.use3dThumbs
 * @param {object} props.pricesVisibility - parsed visibility flags
 * @param {function} props.onTogglePin - (uuid) => void
 * @param {function} props.onOpenProduct - (product) => void
 * @param {function} props.onInquiry - (product) => void
 */
export function FavoritesCompareBlock({
  isMobile = false,
  pinnedProducts = [],
  compareRows = [],
  projectDataUrl = null,
  compartmentTypes = [],
  logoSrc = null,
  use3dThumbs = false,
  pricesVisibility = null,
  onTogglePin = () => {},
  onOpenProduct = () => {},
}) {
  const { t } = useI18n()
  const getProductsCategoryString = useProductStrings()
  const columnsSegmentRef = useRef(null)

  const maxPinned = isMobile ? MAX_PINNED_FAVORITES_MOBILE : MAX_PINNED_FAVORITES_DESKTOP

  const placeholdersCount = Math.max(0, maxPinned - pinnedProducts.length)
  const shouldEqualizeRows = pinnedProducts.length > 1

  const syncRowHeights = useCallback(() => {
    const root = columnsSegmentRef.current
    if (!root) return

    const cells = Array.from(root.querySelectorAll('[data-item]'))
    if (!cells.length) return

    // Reset before measure so shorter rows can shrink after content changes.
    cells.forEach((cell) => {
      cell.style.minHeight = ''
    })

    const maxByRowId = new Map()
    cells.forEach((cell) => {
      const rowId = cell.getAttribute('data-item')
      if (!rowId) return
      const h = Math.ceil(cell.getBoundingClientRect().height)
      const prev = maxByRowId.get(rowId) ?? 0
      if (h > prev) maxByRowId.set(rowId, h)
    })

    cells.forEach((cell) => {
      const rowId = cell.getAttribute('data-item')
      if (!rowId) return
      const maxH = maxByRowId.get(rowId)
      if (!maxH) return
      cell.style.minHeight = `${maxH}px`
    })
  }, [])

  useLayoutEffect(() => {
    const root = columnsSegmentRef.current
    if (!root) return

    const clearMinHeights = () => {
      root.querySelectorAll('[data-item]').forEach((cell) => {
        cell.style.minHeight = ''
      })
    }

    if (!shouldEqualizeRows) {
      clearMinHeights()
      return
    }

    let rafId = 0
    const scheduleSync = () => {
      if (rafId) cancelAnimationFrame(rafId)
      rafId = requestAnimationFrame(() => {
        syncRowHeights()
      })
    }

    const ro = new ResizeObserver(() => {
      scheduleSync()
    })

    ro.observe(root)
    root.querySelectorAll('[data-item]').forEach((cell) => ro.observe(cell))

    scheduleSync()

    return () => {
      if (rafId) cancelAnimationFrame(rafId)
      ro.disconnect()
      clearMinHeights()
    }
  }, [shouldEqualizeRows, pinnedProducts, compareRows, syncRowHeights])

  return (
    <div className={styles.compareBlock}>
      <div className={styles.topBar}>
        <span className={styles.compareHeading}>
          {pinnedProducts.length 
            ? getProductsCategoryString('compare_few', 'default') 
            : t('favorites.pin_and_compare')}
        </span>
      </div>
      { pinnedProducts.length != 0 && (
        <div className={styles.compareColumnsSegment}
          ref={columnsSegmentRef}
          style={{ '--maxColumns': maxPinned }}
        >
          {pinnedProducts.map((product) => (
            <CompareColumn
              key={product.uuid}
              product={product}
              compareRows={compareRows}
              compartmentTypes={compartmentTypes}
              projectDataUrl={projectDataUrl}
              logoSrc={logoSrc}
              use3dThumbs={use3dThumbs}
              pricesVisibility={pricesVisibility}
              onClose={onTogglePin}
              onOpenProduct={onOpenProduct}
            />
          ))}

          {/* Placeholder cards for remaining slots */}
          {Array.from({ length: placeholdersCount }).map((_, idx) => (
            <PlaceholderCard key={`placeholder-${idx}`} maxPinned={maxPinned} />
          ))}
        </div>
      )}
    </div>
  )
}
